import React, { useEffect, useState } from 'react';
import { format, parse } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';

import FilterListIcon from '@mui/icons-material/FilterList';

import { DEFAULT_PAGE_LOAD_SIZE } from '../../api/pagination';
import { contractDetailsById } from '../../fixtures/entities/directusService/contractDetailsByIdFixture';
import { servicesByContractId } from '../../fixtures/entities/directusService/servicesByContractIdFixture';
import {
    loadAppointmentTypeDetails,
    loadAttendanceDetails,
    loadContractDetailsForContractId,
    loadLocationsByServices,
    loadParticipantStatusDetails,
    loadParticipantStatusReasonDetails,
    loadServiceDetailsForContractId,
    loadTimelineStatusDetails,
    loadTimelineTypeDetails,
    loadVacancyStageDetails
} from '../../store/directusService';
import {
    clearParticipantTimelineEvents,
    deleteParticipantTimelineEvent,
    loadParticipantTimelineEvent
} from '../../store/participantService';
import {
    clearVacancySubmissions,
    loadRecruitmentParticipantTimelineEvent,
    loadVacancySubmissionsByParticipantId
} from '../../store/recruitmentService';
import { selectUsersForNames } from '../../store/userSelectors';
import {
    loadUser,
    loadUsersByServiceIds,
    loadUsersMinimalDetails,
    searchUsersByLoggedInUserServiceIds
} from '../../store/userService';
import { getNameFromId } from '../../utils/directusFunctions';
import { uuid } from '../../utils/stringUtils';
import {
    ADVISER,
    AUDIT,
    hasRole,
    LOCAL_ADMIN,
    MANAGER,
    PRAP,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../utils/userRoles';
import Button from '../formElements/Button';
import DateRangePicker from '../formElements/DateRangePicker';
import ParticipantStickyMenu from '../navigation/participant/ParticipantStickyMenu';
import LoadingSpinner from '../ui/LoadingSpinner';

import TimelineFilter from './timelineFilters/TimelineFilter';
import TimelineEditor from './TimelineEditor';
import TimelineView from './TimelineView';

import app from '../../app.module.css';
import timelineClasses from './timelineStyles/timeline.module.css';

const acceptedRoles = [ADVISER, AUDIT, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];
const filterRoles = [ADVISER, LOCAL_ADMIN, MANAGER, PRAP, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];

const Timeline = () => {
    // HOOKS
    const dispatch = useDispatch();

    // LOCAL STATE
    const [openTimelineEditor, setOpenTimelineEditor] = useState(false);
    const [openTimelineFilterEditor, setOpenTimelineFilterEditor] = useState(false);
    const [rows, setRows] = useState([]);
    const [savedRows, setSavedRows] = useState([]);
    const [currentRowId, setCurrentRowId] = useState('');
    const [savedHistory, setSavedHistory] = useState(false);
    const [participantEventsLoaded, setParticipantEventsLoaded] = useState(false);
    const [usersLoaded, setUsersLoaded] = useState(false);
    const [recruitmentParticipantEventsProcessed, setRecruitmentParticipantEventsProcessed] =
        useState(false);
    const [vacancySubmissionsHaveBeenLoaded, setVacancySubmissionsHaveBeenLoaded] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterTerm, setFilterTerm] = useState('');
    const [filters, setFilters] = useState('');
    const [filterType, setFilterType] = useState('');
    const [participantTimelineEventsForTimeline, setParticipantTimelineEventsForTimeline] =
        useState([]);

    const addTimeline = () => {
        onOpenTimelineEditor(true);
    };

    const onOpenTimelineEditor = (setOpen) => {
        setOpenTimelineEditor(setOpen);
    };

    const onSearchDateRange = (searchRange) => {
        if (searchRange) {
            const inDate = parse(searchRange.substring(0, 10), 'dd/MM/yyyy', new Date());
            const startDate = format(inDate, 'yyyy-MM-dd');
            const outDate = parse(searchRange.substring(13), 'dd/MM/yyyy', new Date());
            const endDate = format(outDate, 'yyyy-MM-dd');
            setSearchTerm(startDate + endDate);
        } else {
            setSearchTerm('');
        }
    };

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const appointmentTypeDetails = useSelector(
        (state) => state.entities.directusService.appointmentTypeDetails
    );
    const attendanceDetails = useSelector(
        (state) => state.entities.directusService.attendanceDetails
    );
    const participantStatusDetails = useSelector(
        (state) => state.entities.directusService.participantStatusDetails
    );
    const participantStatusReasonDetails = useSelector(
        (state) => state.entities.directusService.participantStatusReasonDetails
    );
    const timelineStatusDetails = useSelector(
        (state) => state.entities.directusService.timelineStatusDetails
    );
    const timelineTypeDetails = useSelector(
        (state) => state.entities.directusService.timelineTypeDetails
    );
    const vacancyStageDetails = useSelector(
        (state) => state.entities.directusService.vacancyStageDetails
    );
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const participantTimelineEvent = useSelector(
        (state) => state.entities.participantService.participantTimelineEvent
    );
    const participantTimelineEvents = useSelector(
        (state) => state.entities.participantService.participantTimelineEvents
    );
    const participantTimelineEventsMetaData = useSelector(
        (state) => state.entities.participantService.participantTimelineEventsMetaData
    );
    const recruitmentParticipantTimelineEvents = useSelector(
        (state) => state.entities.recruitmentService.recruitmentParticipantTimelineEvents
    );
    const vacancySubmissions = useSelector(
        (state) => state.entities.recruitmentService.vacancySubmissions
    );
    const users = useSelector(selectUsersForNames);
    const usersMetaData = useSelector(
        (state) => state.entities.userService.usersByLoggedInUserServiceIdsMetaData
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USEEFFECTS
    useEffect(() => {
        appointmentTypeDetails?.length < 1 && dispatch(loadAppointmentTypeDetails());
        attendanceDetails?.length < 1 && dispatch(loadAttendanceDetails());
        participantStatusDetails?.length < 1 && dispatch(loadParticipantStatusDetails());
        participantStatusReasonDetails?.length < 1 &&
            dispatch(loadParticipantStatusReasonDetails());
        timelineStatusDetails?.length < 1 && dispatch(loadTimelineStatusDetails());
        timelineTypeDetails?.length < 1 && dispatch(loadTimelineTypeDetails());
        vacancyStageDetails?.length < 1 && dispatch(loadVacancyStageDetails());

        return () => {
            return () => cleanUp();
        };
    }, []);

    useEffect(() => {
        if (Object.keys(currentParticipant).length < 1) return;

        dispatch(loadParticipantTimelineEvent({ participantIds: [currentParticipant.id] }));
        dispatch(clearVacancySubmissions());
        dispatch(loadVacancySubmissionsByParticipantId(currentParticipant.id));

        if (!(currentParticipant.contractId in contractDetailsById))
            dispatch(loadContractDetailsForContractId(currentParticipant.contractId));
        if (!(currentParticipant.contractId in servicesByContractId))
            dispatch(loadServiceDetailsForContractId(currentParticipant.contractId));
    }, [currentParticipant]);

    useEffect(() => {
        if (!('serviceIds' in loggedInUser)) return;
        if (loggedInUser.serviceIds.length > 0) {
            dispatch(loadLocationsByServices(loggedInUser.serviceIds));
            users?.length < 1 && dispatch(loadUsersByServiceIds(loggedInUser.serviceIds));
        }
    }, [loggedInUser.id]);

    useEffect(() => {
        if (
            participantTimelineEventsForTimeline?.length < 1 &&
            participantTimelineEvents?.length < 1
        )
            return;

        setParticipantTimelineEventsForTimeline(participantTimelineEvents);

        if (!participantTimelineEventsMetaData.last)
            dispatch(
                loadParticipantTimelineEvent(
                    { participantIds: [currentParticipant.id] },
                    participantTimelineEventsMetaData.number + 1,
                    DEFAULT_PAGE_LOAD_SIZE
                )
            );
    }, [participantTimelineEvents, participantTimelineEventsMetaData]);

    useEffect(() => {
        if (participantTimelineEventsForTimeline?.length < 101) return;

        sortRows(rows);
    }, [participantTimelineEventsForTimeline]);

    useEffect(() => {
        if (!users || users.length < 1 || usersLoaded) return;

        if (usersMetaData?.last) {
            setUsersLoaded(true);
            const userIds = [...new Set(rows?.map((el) => el.userId))].filter((el) => el);
            const missingUserIds = userIds.filter(
                (el) => !users.find((entry) => entry.id === el || el.userId !== 'Polaris System')
            );
            missingUserIds.length > 0
                ? dispatch(loadUsersMinimalDetails(missingUserIds))
                : configRows();
        } else {
            dispatch(
                searchUsersByLoggedInUserServiceIds(
                    loggedInUser.serviceIds,
                    !users.length ? 0 : usersMetaData.number + 1
                )
            );
        }
    }, [users]);

    useEffect(() => {
        if (!vacancySubmissionsHaveBeenLoaded) return;

        if (
            vacancySubmissions?.length > 0 &&
            participantEventsLoaded &&
            participantTimelineEventsMetaData.last &&
            !recruitmentParticipantEventsProcessed
        ) {
            dispatch(loadRecruitmentParticipantTimelineEvent(currentParticipant.id));
        } else if (vacancySubmissions?.length === 0 && participantEventsLoaded) {
            setRecruitmentParticipantEventsProcessed(true);
        }
    }, [
        vacancySubmissionsHaveBeenLoaded,
        participantEventsLoaded,
        participantTimelineEventsMetaData?.last
    ]);

    useEffect(() => {
        const sortedRows = !searchTerm
            ? rows
            : rows.filter(
                  (el) =>
                      el.eventDate >= searchTerm.substring(0, 10) &&
                      el.eventDate <= searchTerm.substring(10)
              );
        const sortedFilterRows = sortedRows
            .map((el) => {
                const dateForSort = el.eventDate;
                const timeForSort =
                    el.eventTime.length === 3
                        ? `0${el.eventTime.substring(0, 1)}:${el.eventTime.substring(1)}`
                        : `${el.eventTime.substring(0, 2)}:${el.eventTime.substring(2)}`;
                const dateTime = `${dateForSort}${timeForSort}`;
                return { ...el, dateTime: dateTime };
            })
            .sort((a, b) => b.dateTime.localeCompare(a.dateTime));
        setSavedRows(sortedFilterRows);
    }, [searchTerm]);

    useEffect(() => {
        let sortedRows;

        if (!filterTerm) sortedRows = rows;
        else if (filterType === 'Appointments') sortedRows = filterAppointments();
        else sortedRows = rows;

        const sortedFilterRows = sortedRows
            .map((el) => {
                const dateForSort = el.eventDate;
                const timeForSort =
                    el.eventTime.length === 3
                        ? `0${el.eventTime.substring(0, 1)}:${el.eventTime.substring(1)}`
                        : `${el.eventTime.substring(0, 2)}:${el.eventTime.substring(2)}`;
                const dateTime = `${dateForSort}${timeForSort}`;
                return { ...el, dateTime: dateTime };
            })
            .sort((a, b) => b.dateTime.localeCompare(a.dateTime));
        setSavedRows(sortedFilterRows);
    }, [filterTerm]);

    useEffect(() => {
        if (successMessage.startsWith('Vacancy submissions have been loaded')) {
            setVacancySubmissionsHaveBeenLoaded(true);
        } else if (successMessage === 'TimelineEvents have been loaded') {
            setRows(
                participantTimelineEvents.map((el) => {
                    let emailAddress = '';
                    if (el.userId === 'Polaris System') {
                        emailAddress = 'Polaris System';
                    } else if (users && users.length) {
                        emailAddress =
                            users.find((entry) => entry.id === el.userId)?.loginEmailAddress || '';
                    }
                    if (el.eventType === 'Outbound Communication') {
                        return {
                            ...el,
                            emailAddress: emailAddress ? emailAddress : '',
                            details: el.communicationEvent?.details || '',
                            subject: el.communicationEvent?.subject || '',
                            recipient: el.communicationEvent?.recipient || '',
                            attachments: el.communicationEvent?.attachments || []
                        };
                    } else if (el?.history) {
                        return el.history?.length > 0
                            ? {
                                  ...el,
                                  emailAddress: emailAddress ? emailAddress : '',
                                  eventHistory: el.history
                              }
                            : {
                                  ...el,
                                  emailAddress: emailAddress ? emailAddress : '',
                                  eventHistory: []
                              };
                    } else {
                        return {
                            ...el,
                            emailAddress: emailAddress ? emailAddress : ''
                        };
                    }
                })
            );
            setParticipantEventsLoaded(true);
        } else if (successMessage === 'Recruitment Participant TimelineEvents have been loaded') {
            setRecruitmentParticipantEventsProcessed(true);
            const temp = recruitmentParticipantTimelineEvents.map((el) => {
                let emailAddress = '';
                if (el.userId === 'Polaris System') {
                    emailAddress = 'Polaris System';
                } else if (users && users.length) {
                    emailAddress =
                        users?.find((entry) => entry.id === el.history[0].userId)
                            ?.loginEmailAddress || '';
                }
                return {
                    ...el,
                    id: uuid(),
                    eventDate: el.history[0].eventDate.substring(0, 10),
                    eventTime: `${el.history[0].eventDate.substring(11, 13)}${el.history[0].eventDate.substring(14, 16)}`,
                    eventType: el.history[0].eventType,
                    vacancyStage: getNameFromId(vacancyStageDetails, el.history[0].newValue),
                    vacancyName: el.vacancyTitle,
                    businessName: el.businessRecordName,
                    notes: vacancySubmissions.find((entry) => entry.vacancyId === el.vacancyId)
                        ?.rejection?.feedback,
                    emailAddress: emailAddress ? emailAddress : ''
                };
            });
            if (temp?.length > 0) {
                setRows((prev) => [...prev, ...temp]);
            }
        } else if (successMessage === 'Timeline entry has been added') {
            let updatedParticipantTimelineEvent = participantTimelineEvent;

            if (users && users.length) {
                const emailAddress =
                    users.find((entry) => entry.id === participantTimelineEvent.userId)
                        ?.loginEmailAddress || '';
                if (!emailAddress) dispatch(loadUser(participantTimelineEvent.userId));
                else {
                    updatedParticipantTimelineEvent = {
                        ...participantTimelineEvent,
                        emailAddress
                    };
                }
            } else dispatch(loadUser(participantTimelineEvent.userId));

            setRows((prev) => [updatedParticipantTimelineEvent, ...prev]);
            setSavedRows((prev) => [updatedParticipantTimelineEvent, ...prev]);
        } else if (successMessage === 'Timeline entry has been deleted') {
            const events = rows.filter((el) => el.id !== currentRowId);
            setRows(events);
            setSavedRows(events);
            setCurrentRowId('');
        }
    }, [successMessage]);

    useEffect(() => {
        if (
            !recruitmentParticipantEventsProcessed ||
            !participantEventsLoaded ||
            ('number' in participantTimelineEventsMetaData &&
                participantTimelineEventsMetaData.last)
        )
            return;

        const userIds = [...new Set(rows?.map((el) => el.userId))].filter((el) => el);
        const missingUserIds = userIds.filter(
            (el) => !users.find((entry) => entry.id === el || el.userId !== 'Polaris System')
        );
        missingUserIds.length > 0
            ? dispatch(loadUsersMinimalDetails(missingUserIds))
            : configRows();
    }, [
        recruitmentParticipantEventsProcessed,
        participantEventsLoaded,
        participantTimelineEventsMetaData,
        users
    ]);

    // HELPER FNS
    const sortRows = (rows) => {
        const sortedFilterRows = rows
            .map((el) => {
                const dateForSort = el.eventDate;
                const timeForSort =
                    el.eventTime.length === 3
                        ? `0${el.eventTime.substring(0, 1)}:${el.eventTime.substring(1)}`
                        : `${el.eventTime.substring(0, 2)}:${el.eventTime.substring(2)}`;
                const dateTime = `${dateForSort}${timeForSort}`;
                return { ...el, dateTime: dateTime };
            })
            .sort((a, b) => b.dateTime.localeCompare(a.dateTime));
        setRows(sortedFilterRows);
        setSavedRows(sortedFilterRows);
    };

    const configRows = () => {
        const updatedRows = rows.map((el) => {
            if (el.emailAddress === 'Polaris System') return el;
            else {
                const emailAddress = users.find(
                    (entry) => entry.id === el.userId
                )?.loginEmailAddress;
                return {
                    ...el,
                    emailAddress
                };
            }
        });
        setRows(updatedRows);
        setSavedRows(updatedRows);
    };

    const filterAppointments = () => {
        switch (filters) {
            case '123':
                return rows
                    .filter((el) => el.eventType === filterTerm.appointmentTypeId.name)
                    .filter((el) => el.userId === filterTerm.userId.id)
                    .filter((el) => el.eventStatusId === filterTerm.attendanceStatusId.id);
            case '12':
                return rows
                    .filter((el) => el.eventType === filterTerm.appointmentTypeId.name)
                    .filter((el) => el.userId === filterTerm.userId.id);
            case '13':
                return rows
                    .filter((el) => el.eventType === filterTerm.appointmentTypeId.name)
                    .filter((el) => el.eventStatusId === filterTerm.attendanceStatusId.id);
            case '23':
                return rows
                    .filter((el) => el.userId === filterTerm.userId.id)
                    .filter((el) => el.eventStatusId === filterTerm.attendanceStatusId.id);
            case '1':
                return rows.filter((el) => el.eventType === filterTerm.appointmentTypeId.name);
            case '2':
                return rows
                    .filter((el) => el.userId === filterTerm.userId.id)
                    .filter((el) => el.eventType !== 'PARTICIPANT');
            case '3':
                return rows.filter((el) => el.eventStatusId === filterTerm.attendanceStatusId.id);
            default:
                return '';
        }
    };

    const cleanUp = () => {
        dispatch(clearParticipantTimelineEvents());
    };

    const onClearFilters = () => {
        setFilterTerm('');
        setFilters('');
        setFilterType('');
    };

    // EVENT HANDLERS
    const onDelete = (id) => {
        setCurrentRowId(id);
        dispatch(deleteParticipantTimelineEvent(id));
    };

    const onPresentFilters = (selects, filters, type) => {
        setFilterTerm(selects);
        setFilters(filters);
        setFilterType(type);
    };

    // AWAITING CONTENT
    if (rows?.length < 1 || !participantEventsLoaded) {
        return <LoadingSpinner content="No timeline found" time={3} />;
    } else {
        // We need to sort & save rows for searches first time through
        if (!savedHistory && participantEventsLoaded && recruitmentParticipantEventsProcessed) {
            sortRows(rows);
            setSavedHistory(true);
        }
    }

    // RENDER
    return (
        <div className={app.withSideBar} data-testid="form_start">
            <div className={timelineClasses.timelineToolBar}>
                <div className={timelineClasses.timelineDateRangePicker}>
                    <DateRangePicker onSearch={onSearchDateRange} />
                </div>
                <div className={timelineClasses.filter}>
                    <Button
                        id="addTimelineFilterButton"
                        disabled={
                            !hasRole(filterRoles, loggedInUser.roles) ||
                            filterTerm ||
                            !participantEventsLoaded
                        }
                        content="Add Filters"
                        type="button"
                        onClick={() => setOpenTimelineFilterEditor(true)}
                        icon={true}
                        iconType={<FilterListIcon />}
                    />
                    {openTimelineFilterEditor && (
                        <TimelineFilter
                            rows={rows}
                            isOpen={openTimelineFilterEditor}
                            filterRoles={filterRoles}
                            onPresentFilters={onPresentFilters}
                            onClose={() => setOpenTimelineFilterEditor(false)}
                        />
                    )}

                    <div
                        hidden={!filterTerm}
                        className={`${timelineClasses.link} ${timelineClasses.filtersLink}`}
                        onClick={() => onClearFilters()}>
                        Clear Filters x
                    </div>
                </div>
                <div className={timelineClasses.toolBarButton}>
                    <Button
                        id="addTimelineCreateButton"
                        disabled={!hasRole(acceptedRoles, loggedInUser.roles)}
                        content="Create Timeline Entry"
                        type="button"
                        onClick={addTimeline}
                        icon={true}
                    />
                </div>
                <TimelineEditor
                    isOpen={openTimelineEditor}
                    acceptedRoles={acceptedRoles}
                    onOpenTimelineEditor={onOpenTimelineEditor}
                    timelineStatusDetails={timelineStatusDetails}
                />
            </div>
            <div className={timelineClasses.timelineBody}>
                {savedRows?.length < 1 ? (
                    <div className={timelineClasses.noTimelines}>
                        <LoadingSpinner content="No timeline found" />
                    </div>
                ) : (
                    savedRows.map((el) => (
                        <div key={el.id} className={app.fadein}>
                            <TimelineView
                                row={el}
                                viewAppointmentRoles={filterRoles}
                                onDelete={onDelete}
                            />
                        </div>
                    ))
                )}
            </div>
            <div className={app.sideBar}>
                <ParticipantStickyMenu />
            </div>
        </div>
    );
};

export default Timeline;
